import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { useApi } from '@elparking/utils'
import cancelBooking from 'commons/js/api/rentalCar/cancelBooking'
import { Button, Copy, Flex, WebviewWrapper, InfoMessage } from '@elparking/components'
import Calendar from '../Icons/Calendar'
import EditBookingModal from '../Modals/EditBookingModal'
import SimpleModal from '../Modals/SimpleModal'
import { RentalCarEvents, trackTealiumEvent } from 'commons/js/services/analytics'
import useOnCloseModalAnalyticEvent from 'commons/js/hooks/useOnCloseModalAnalyticEvent'

const DefaultButton = ({ disableButton, onClick }) => <Button.unstyled
  dataTest='edit-button-rental-car'
  borderRadius='3px'
  padding='1em 0.5em'
  fontSize='small'
  fontColor={disableButton ? 'gray80' : 'main'}
  height='auto'
  disabled={disableButton}
  width={['100%', '180px']}
  textTransform='none'
  fontWeight='medium'
  onClick={onClick}>
    <Flex gridGap='0.5em'>
        <Calendar color={disableButton ? 'gray80' : 'main'} height='16px' />
        <FormattedMessage defaultMessage='Modificar reserva' />
    </Flex>
</Button.unstyled>

DefaultButton.propTypes = {
    disableButton: PropTypes.bool.isRequired,
    onClick: PropTypes.func.isRequired,
}
DefaultButton.defaultProps = {
    disableButton: false,
}

const EditButton = ({ onSuccess, cancelBooking, id, button: StyledButton, token, pageName }) => {
    const [showEditBookingModal, setShowEditBookingModal] = useState(false)
    const [showErrorModal, setShowErrorModal] = useState(false)
    const {
        response: { result, error },
        loading,
        sendRequest: cancel,
    } = useApi(cancelBooking)

    useEffect(() => {
        showEditBookingModal && trackTealiumEvent(RentalCarEvents.showModifyRentalCarBooking(pageName))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showEditBookingModal])

    useEffect(() => {
        if (result && !error) {
            onSuccess(result)
        } else if (error) {
            setShowErrorModal(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result, error])

    useOnCloseModalAnalyticEvent(showEditBookingModal, RentalCarEvents.showBookingDetail(pageName))
    useOnCloseModalAnalyticEvent(showErrorModal, RentalCarEvents.showBookingDetail(pageName))
    const disableButton = loading || error || result

    return <Flex>
        <WebviewWrapper fallbackComponent={
            <EditBookingModal
              isShown={showEditBookingModal}
              onClose={() => {
                    trackTealiumEvent(RentalCarEvents.keepModifyButtonRentalCarBooking)
                    setShowEditBookingModal(false)
                }}
              onCancelBooking={() => {
                    trackTealiumEvent(RentalCarEvents.cancelModifyButtonRentalCarBooking)
                    setShowEditBookingModal(false)
                    cancel({ id: id, token: token })
                }}
              pageName={pageName} />}>
            <InfoMessage
              rollUpMessage
              animated
              show={showEditBookingModal}
              title={<FormattedMessage defaultMessage='¿Necesitas modificar tu reserva?' />}
              message={<Copy fontSize='medium' fontColor='darkGray' fontWeight='regular'><FormattedMessage defaultMessage='Para modificar tu reserva, debes cancelar la que ya tienes y realizar una nueva.' /></Copy>}
              actionButton={() => {
                    trackTealiumEvent(RentalCarEvents.cancelModifyButtonRentalCarBooking(pageName))
                    setShowEditBookingModal(false)
                    cancel({ id: id, token: token })
                }}
              textButton={<FormattedMessage defaultMessage='Cancelar y volver a reservar' />}
              onClose={() => {
                    trackTealiumEvent(RentalCarEvents.keepModifyButtonRentalCarBooking(pageName))
                    setShowEditBookingModal(false)
}}
              secondaryButton={<Button.bordered marginTop='1em' width='100%' onClick={() => {
                    trackTealiumEvent(RentalCarEvents.keepModifyButtonRentalCarBooking(pageName))
                    setShowEditBookingModal(false)
                }}><FormattedMessage defaultMessage='No, mantener' /></Button.bordered>}
              backgroundPosition='fixed' />
        </WebviewWrapper>
        <SimpleModal
          isShown={showErrorModal}
          title={<FormattedMessage defaultMessage='No es posible cancelar la reserva' />}
          subtitle={<FormattedMessage defaultMessage='Puede intentarlo más tarde' />}
          onAccept={() => setShowErrorModal(false)}
          onClose={() => setShowErrorModal(false)}
          showCloseButton />
        <StyledButton
          disableButton={!!disableButton}
          onClick={() => {
                trackTealiumEvent(RentalCarEvents.modifyButtonRentalCarBooking(pageName))
                setShowEditBookingModal(true)
            }} />
    </Flex>
}

EditButton.propTypes = {
    cancelBooking: PropTypes.func,
    onSuccess: PropTypes.func,
    id: PropTypes.any,
    button: PropTypes.func,
    token: PropTypes.string,
    pageName: PropTypes.string,
}

EditButton.defaultProps = {
    button: DefaultButton,
    cancelBooking,
    onSuccess: () => { },
}

export default EditButton
